import type {
  ObservabilityCountBucket,
  ObservabilityUsageProjectItem,
  ObservabilityUsageUserItem,
  ObservabilityUserAccessItem,
} from '@scatterin/api-types';
import { Activity, FolderKanban, HardDrive, Users, Workflow } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { SearchInput } from '@/components/filterChips';
import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UsageSubjectDrawer } from '@/components/usageSubjectDrawer';
import {
  usePreviousSummaryQuery,
  useSummaryQuery,
  useUsageInventoryQuery,
  useUsageSubjectQuery,
  useUsageUserIdentitiesQuery,
} from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatBytes, formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { matchesSearch } from '@/lib/search';

type UsageView = 'overview' | 'projects' | 'storage' | 'users' | 'workflows';

const usageViews = ['overview', 'users', 'projects', 'storage', 'workflows'] as const;

const currentUsageView = (search: string): UsageView => {
  const view = new URLSearchParams(search).get('view');
  if (view === 'inventory') return 'users';
  return usageViews.includes(view as UsageView) ? (view as UsageView) : 'overview';
};

const updateView = (current: string, view: UsageView): string => {
  const search = new URLSearchParams(current);
  if (view === 'overview') search.delete('view');
  else search.set('view', view);
  return search.toString();
};

const openSubjectSearch = (current: string, subjectType: 'project' | 'user', subjectId: string): string => {
  const search = new URLSearchParams(current);
  search.delete(subjectType === 'project' ? 'userId' : 'projectId');
  search.set(subjectType === 'project' ? 'projectId' : 'userId', subjectId);
  return search.toString();
};

const closeSubjectSearch = (current: string): string => {
  const search = new URLSearchParams(current);
  search.delete('projectId');
  search.delete('userId');
  return search.toString();
};

const comparisonLabel = (current: number, previous: number | undefined): string => {
  if (previous === undefined) return 'Previous period unavailable';
  if (previous === 0 && current === 0) return 'No activity in either period';
  if (previous === 0) return 'New activity vs previous period';
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < 0.5) return 'Level with previous period';
  return `${change > 0 ? '+' : ''}${change.toLocaleString(undefined, { maximumFractionDigits: 0 })}% vs previous period`;
};

const formatActivityValue = (label: string, value: number): string => (label === 'Storage' ? formatBytes(value) : formatInteger(value));

const formatUsd = (value: number | undefined): string =>
  (value ?? 0).toLocaleString(undefined, {
    currency: 'USD',
    maximumFractionDigits: value && value < 0.01 ? 6 : 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });

const humanize = (value: string): string =>
  value
    .replaceAll('_', ' ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll(/^./g, (character) => character.toUpperCase());

const ComparisonCard = ({
  current,
  format = formatInteger,
  icon,
  label,
  previous,
}: {
  current: number;
  format?: (value: number) => string;
  icon: ReactNode;
  label: string;
  previous?: number;
}) => (
  <Card>
    <CardContent className="grid gap-2">
      <div className="text-muted flex items-center gap-2 text-xs font-semibold">
        {icon}
        {label}
      </div>
      <p className="text-ink numeric text-2xl font-semibold">{format(current)}</p>
      <p className="text-muted text-xs">{comparisonLabel(current, previous)}</p>
    </CardContent>
  </Card>
);

const RankedBuckets = ({
  buckets,
  emptyTitle,
  format = formatInteger,
  noun,
}: {
  buckets: ObservabilityCountBucket[];
  emptyTitle: string;
  format?: (value: number) => string;
  noun: string;
}) => {
  const maximum = Math.max(...buckets.map((bucket) => bucket.count ?? 0), 0);
  if (buckets.length === 0) {
    return (
      <EmptyState title={emptyTitle} tone="coverage">
        This ranking appears only when attributed run evidence exists in the selected period.
      </EmptyState>
    );
  }
  return (
    <ol className="grid gap-3">
      {buckets.map((bucket, index) => (
        <li className="grid gap-1.5" key={bucket.key}>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-ink min-w-0 font-medium">
              <span className="text-muted numeric mr-2">{index + 1}</span>
              {bucket.key}
            </span>
            <span className="text-muted numeric shrink-0">{format(bucket.count ?? 0)}</span>
          </div>
          <div className="bg-line h-1.5 overflow-hidden rounded-full">
            <div
              className="bg-chart-secondary h-full rounded-full"
              style={{ width: `${maximum ? Math.max(((bucket.count ?? 0) / maximum) * 100, 2) : 0}%` }}
            />
          </div>
          <p className="text-muted text-xs">{noun}</p>
        </li>
      ))}
    </ol>
  );
};

const UsageSearch = ({ label, onChange, value }: { label: string; onChange: (value: string) => void; value: string }) => (
  <label className="text-muted grid max-w-xl gap-1.5 text-xs font-semibold">
    {label}
    <SearchInput onChange={onChange} placeholder={`Search ${label.toLowerCase()}`} value={value} />
  </label>
);

const failureBadge = (count: number | undefined) => <Badge tone={(count ?? 0) > 0 ? 'red' : 'slate'}>{formatInteger(count)} related</Badge>;

const UserIdentity = ({
  identity,
  isLoading,
  item,
}: {
  identity?: ObservabilityUserAccessItem;
  isLoading: boolean;
  item: ObservabilityUsageUserItem;
}) => {
  let identityState = <p className="text-muted mt-1 text-xs">Email unavailable</p>;
  if (isLoading) {
    identityState = (
      <p aria-live="polite" className="text-muted mt-1 text-xs">
        Resolving email…
      </p>
    );
  }
  if (identity?.email) {
    identityState = (
      <p className="text-muted mt-1 truncate text-xs" title={item.ownerUserId}>
        User ID {item.ownerUserId}
      </p>
    );
  }

  return (
    <div className="min-w-0">
      <p className="text-ink truncate font-semibold" title={identity?.email ?? item.ownerUserId}>
        {identity?.email ?? item.ownerUserId}
      </p>
      {identityState}
      <p className="text-muted mt-1 text-xs" title={formatTime(item.latestRunAt)}>
        Last activity {formatRelativeTime(item.latestRunAt)}
      </p>
    </div>
  );
};

const UserCard = ({
  identity,
  isIdentityLoading,
  item,
  search,
}: {
  identity?: ObservabilityUserAccessItem;
  isIdentityLoading: boolean;
  item: ObservabilityUsageUserItem;
  search: string;
}) => (
  <article className="border-line grid gap-3 rounded-lg border p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <UserIdentity identity={identity} isLoading={isIdentityLoading} item={item} />
      {failureBadge(item.relatedFailures)}
    </div>
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <dt className="text-muted text-xs">Runs / workflows</dt>
        <dd className="text-ink numeric mt-1">
          {formatInteger(item.runs)} / {formatInteger(item.workflows)}
        </dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Projects</dt>
        <dd className="text-ink numeric mt-1">{formatInteger(item.projects)}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Files</dt>
        <dd className="text-ink numeric mt-1">{formatInteger(item.inputFiles)}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Storage</dt>
        <dd className="text-ink numeric mt-1">{formatBytes(item.inputBytes)}</dd>
      </div>
    </dl>
    <Button asChild variant="secondary">
      <Link to={{ pathname: '/usage', search: openSubjectSearch(search, 'user', item.ownerUserId) }}>View user 360</Link>
    </Button>
  </article>
);

const ProjectIdentity = ({
  identity,
  isLoading,
  item,
}: {
  identity?: ObservabilityUserAccessItem;
  isLoading: boolean;
  item: ObservabilityUsageProjectItem;
}) => (
  <div className="min-w-0">
    <p className="text-ink truncate font-semibold" title={item.projectId}>
      {item.projectId}
    </p>
    {item.ownerUserId ? (
      <>
        <p aria-live={isLoading ? 'polite' : undefined} className="text-muted mt-1 truncate text-xs" title={identity?.email ?? undefined}>
          Owner {identity?.email ?? (isLoading ? 'Resolving email…' : 'Email unavailable')}
        </p>
        <p className="text-muted mt-1 truncate text-xs" title={item.ownerUserId}>
          User ID {item.ownerUserId}
        </p>
      </>
    ) : (
      <p className="text-muted mt-1 text-xs">Owner not recorded</p>
    )}
  </div>
);

const ProjectCard = ({
  identity,
  isIdentityLoading,
  item,
  search,
}: {
  identity?: ObservabilityUserAccessItem;
  isIdentityLoading: boolean;
  item: ObservabilityUsageProjectItem;
  search: string;
}) => (
  <article className="border-line grid gap-3 rounded-lg border p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <ProjectIdentity identity={identity} isLoading={isIdentityLoading} item={item} />
      {failureBadge(item.relatedFailures)}
    </div>
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <div>
        <dt className="text-muted text-xs">Runs / workflows</dt>
        <dd className="text-ink numeric mt-1">
          {formatInteger(item.runs)} / {formatInteger(item.workflows)}
        </dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Last activity</dt>
        <dd className="text-ink mt-1">{formatRelativeTime(item.latestRunAt)}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Files</dt>
        <dd className="text-ink numeric mt-1">{formatInteger(item.inputFiles)}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs">Storage</dt>
        <dd className="text-ink numeric mt-1">{formatBytes(item.inputBytes)}</dd>
      </div>
    </dl>
    <Button asChild variant="secondary">
      <Link to={{ pathname: '/usage', search: openSubjectSearch(search, 'project', item.projectId) }}>View project 360</Link>
    </Button>
  </article>
);

const identityOwnerIdsForView = (
  view: UsageView,
  users: ObservabilityUsageUserItem[],
  projects: ObservabilityUsageProjectItem[],
): string[] => {
  if (view === 'users') return users.map((user) => user.ownerUserId);
  if (view === 'projects') return projects.flatMap((project) => (project.ownerUserId ? [project.ownerUserId] : []));
  return [];
};

// UsagePage coordinates five evidence views plus URL-addressable subject drawers.
// eslint-disable-next-line sonarjs/cognitive-complexity
export const UsagePage = () => {
  const { setState, state } = useRouteContext();
  const summaryQuery = useSummaryQuery(state);
  const previousQuery = usePreviousSummaryQuery(state);
  const inventoryQuery = useUsageInventoryQuery(state);
  const location = useLocation();
  const navigate = useNavigate();
  const view = currentUsageView(location.search);
  const searchParameters = new URLSearchParams(location.search);
  const selectedProjectId = searchParameters.get('projectId') ?? undefined;
  const selectedUserId = selectedProjectId ? undefined : (searchParameters.get('userId') ?? undefined);
  let selectedSubjectType: 'project' | 'user' | undefined;
  if (selectedProjectId) selectedSubjectType = 'project';
  else if (selectedUserId) selectedSubjectType = 'user';
  const selectedSubjectId = selectedProjectId ?? selectedUserId;
  const subjectQuery = useUsageSubjectQuery(state, selectedSubjectType, selectedSubjectId);
  const inventoryUsers = inventoryQuery.data?.users ?? [];
  const inventoryProjects = inventoryQuery.data?.projects ?? [];
  const identityOwnerUserIds = identityOwnerIdsForView(view, inventoryUsers, inventoryProjects);
  const userIdentitiesQuery = useUsageUserIdentitiesQuery(state, identityOwnerUserIds, view === 'users' || view === 'projects');
  let selectedIdentityLabel: string | undefined;
  if (selectedSubjectType === 'user') {
    selectedIdentityLabel = userIdentitiesQuery.data?.[selectedSubjectId ?? '']?.email ?? undefined;
  } else if (selectedSubjectType === 'project') {
    selectedIdentityLabel = `Project ${selectedSubjectId}`;
  }

  if (summaryQuery.isLoading) return <LoadingState label="Loading usage evidence" />;
  if (summaryQuery.isError) return <ErrorState message={summaryQuery.error.message} onRetry={() => void summaryQuery.refetch()} />;
  if (view === 'overview' && inventoryQuery.isLoading) return <LoadingState label="Loading usage inventory" />;
  if (view === 'overview' && inventoryQuery.isError) {
    return <ErrorState message={inventoryQuery.error.message} onRetry={() => void inventoryQuery.refetch()} />;
  }

  const summary = summaryQuery.data;
  const previous = previousQuery.data;
  const runEvidence = summary?.signalEvidence?.find((evidence) => evidence.signal === 'runs');
  const hasRunEvidence = (runEvidence?.observations ?? 0) > 0;
  const users = inventoryUsers.filter((user) => {
    const identity = userIdentitiesQuery.data?.[user.ownerUserId];
    return matchesSearch(state.search, [user.ownerUserId, identity?.email, identity?.username]);
  });
  const projects = inventoryProjects.filter((project) => {
    const identity = project.ownerUserId ? userIdentitiesQuery.data?.[project.ownerUserId] : undefined;
    return matchesSearch(state.search, [project.projectId, project.ownerUserId, identity?.email, identity?.username]);
  });
  const filtered = Boolean(state.search);

  const inventoryState = (content: ReactNode, empty: boolean, emptyTitle: string): ReactNode => {
    let result = content;
    if (inventoryQuery.isLoading) {
      result = <LoadingState label="Loading usage inventory" />;
    } else if (inventoryQuery.isError) {
      result = (
        <ErrorState message={inventoryQuery.error?.message ?? 'Usage inventory failed'} onRetry={() => void inventoryQuery.refetch()} />
      );
    } else if (empty) {
      result = (
        <EmptyState title={filtered ? 'No inventory rows match this search' : emptyTitle} tone={filtered ? 'filtered' : 'coverage'}>
          {filtered
            ? 'Clear the search or widen the selected period.'
            : 'Run collection has no attributed row for this dimension in the selected period.'}
        </EmptyState>
      );
    }
    return <>{result}</>;
  };

  return (
    <div className="grid min-w-0 gap-5">
      <StateView
        title={
          hasRunEvidence
            ? `Usage is based on ${formatInteger(runEvidence?.observations)} run observations`
            : 'Usage collection has no run observations in this scope'
        }
        tone={hasRunEvidence ? 'healthy' : 'coverage'}
      >
        {runEvidence?.lastReceivedAt
          ? `Last received ${formatRelativeTime(runEvidence.lastReceivedAt)} (${formatTime(runEvidence.lastReceivedAt)}).`
          : 'Without run collection evidence, zero usage is not interpreted as no product activity.'}
      </StateView>

      <Tabs
        onValueChange={(nextView) => void navigate({ pathname: '/usage', search: updateView(location.search, nextView as UsageView) })}
        value={view}
      >
        <div className="overflow-x-auto pb-1">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="storage">Storage</TabsTrigger>
            <TabsTrigger value="workflows">Workflows</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview">
          <div className="grid gap-5">
            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <ComparisonCard
                current={summary?.usage.uniqueUsers ?? 0}
                icon={<Users aria-hidden="true" className="size-4" />}
                label="Active users"
                previous={previous?.usage.uniqueUsers}
              />
              <ComparisonCard
                current={summary?.usage.uniqueProjects ?? 0}
                icon={<FolderKanban aria-hidden="true" className="size-4" />}
                label="Active projects"
                previous={previous?.usage.uniqueProjects}
              />
              <ComparisonCard
                current={summary?.runs.totalRuns ?? 0}
                icon={<Activity aria-hidden="true" className="size-4" />}
                label="Runs"
                previous={previous?.runs.totalRuns}
              />
              <ComparisonCard
                current={inventoryQuery.data?.storage.objects ?? 0}
                icon={<Workflow aria-hidden="true" className="size-4" />}
                label="Stored objects"
              />
              <ComparisonCard
                current={inventoryQuery.data?.storage.bytes ?? 0}
                format={formatBytes}
                icon={<HardDrive aria-hidden="true" className="size-4" />}
                label="Durable storage"
              />
            </section>
            {previousQuery.isError ? (
              <StateView title="Previous-period comparison is unavailable" tone="stale">
                Current usage remains valid; retry to restore the adjacent period comparison.
              </StateView>
            ) : null}
            <Card>
              <CardHeader>
                <CardTitle>Activity direction</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {[
                  ['Users', summary?.usage.uniqueUsers ?? 0, previous?.usage.uniqueUsers],
                  ['Projects', summary?.usage.uniqueProjects ?? 0, previous?.usage.uniqueProjects],
                  ['Runs', summary?.runs.totalRuns ?? 0, previous?.runs.totalRuns],
                  ['Objects', inventoryQuery.data?.storage.objects ?? 0, undefined],
                  ['Storage', inventoryQuery.data?.storage.bytes ?? 0, undefined],
                ].map(([label, current, previousValue]) => (
                  <div className="border-line rounded-md border p-3" key={String(label)}>
                    <p className="text-muted text-xs font-semibold">{label}</p>
                    <p className="text-ink numeric mt-2 text-sm font-semibold">
                      {formatActivityValue(String(label), Number(current))} current
                    </p>
                    <p className="text-muted numeric mt-1 text-xs">
                      {previousValue === undefined
                        ? 'Previous unavailable'
                        : `${formatActivityValue(String(label), Number(previousValue))} previous`}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Ranked workflow mix</CardTitle>
              </CardHeader>
              <CardContent>
                <RankedBuckets
                  buckets={summary?.usage.runsByJobKind ?? []}
                  emptyTitle="No workflow activity was recorded"
                  noun="runs in the selected period"
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="grid gap-4">
            <UsageSearch label="Users" onChange={(search) => setState({ search })} value={state.search} />
            {userIdentitiesQuery.isError ? (
              <StateView title="User email lookup is unavailable" tone="stale">
                Usage remains available with stable user IDs. Retry the page to restore Cognito identity details.
              </StateView>
            ) : null}
            {inventoryState(
              <>
                <div className="grid gap-3 md:hidden">
                  {users.map((user) => (
                    <UserCard
                      identity={userIdentitiesQuery.data?.[user.ownerUserId]}
                      isIdentityLoading={userIdentitiesQuery.isLoading}
                      item={user}
                      key={user.ownerUserId}
                      search={location.search}
                    />
                  ))}
                </div>
                <TableShell className="hidden md:block">
                  <Table aria-label="Usage by user" className="table-fixed" style={{ minWidth: '68rem' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User / last activity</TableHead>
                        <TableHead className="w-36">Runs / workflows</TableHead>
                        <TableHead className="w-24">Projects</TableHead>
                        <TableHead className="w-32">Files</TableHead>
                        <TableHead className="w-32">Storage</TableHead>
                        <TableHead className="w-32">Failures</TableHead>
                        <TableHead className="w-32 text-right">Investigate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.ownerUserId}>
                          <TableCell>
                            <UserIdentity
                              identity={userIdentitiesQuery.data?.[user.ownerUserId]}
                              isLoading={userIdentitiesQuery.isLoading}
                              item={user}
                            />
                          </TableCell>
                          <TableCell className="numeric">
                            {formatInteger(user.runs)} / {formatInteger(user.workflows)}
                          </TableCell>
                          <TableCell className="numeric">{formatInteger(user.projects)}</TableCell>
                          <TableCell className="numeric">{formatInteger(user.inputFiles)}</TableCell>
                          <TableCell className="numeric">{formatBytes(user.inputBytes)}</TableCell>
                          <TableCell>{failureBadge(user.relatedFailures)}</TableCell>
                          <TableCell className="text-right">
                            <Button asChild variant="ghost">
                              <Link
                                to={{
                                  pathname: '/usage',
                                  search: openSubjectSearch(location.search, 'user', user.ownerUserId),
                                }}
                              >
                                View 360
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableShell>
              </>,
              users.length === 0,
              'No user-attributed usage was recorded',
            )}
          </div>
        </TabsContent>

        <TabsContent value="projects">
          <div className="grid gap-4">
            <UsageSearch label="Projects" onChange={(search) => setState({ search })} value={state.search} />
            {userIdentitiesQuery.isError ? (
              <StateView title="Project owner email lookup is unavailable" tone="stale">
                Project usage remains available with stable owner IDs. Retry the page to restore Cognito identity details.
              </StateView>
            ) : null}
            {inventoryState(
              <>
                <div className="grid gap-3 md:hidden">
                  {projects.map((project) => (
                    <ProjectCard
                      identity={project.ownerUserId ? userIdentitiesQuery.data?.[project.ownerUserId] : undefined}
                      isIdentityLoading={userIdentitiesQuery.isLoading}
                      item={project}
                      key={`${project.projectId}:${project.ownerUserId ?? 'unknown'}`}
                      search={location.search}
                    />
                  ))}
                </div>
                <TableShell className="hidden md:block">
                  <Table aria-label="Usage by project" className="table-fixed" style={{ minWidth: '68rem' }}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Project / owner</TableHead>
                        <TableHead className="w-36">Last activity</TableHead>
                        <TableHead className="w-36">Runs / workflows</TableHead>
                        <TableHead className="w-28">Files</TableHead>
                        <TableHead className="w-32">Storage</TableHead>
                        <TableHead className="w-32">Failures</TableHead>
                        <TableHead className="w-32 text-right">Investigate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projects.map((project) => (
                        <TableRow key={`${project.projectId}:${project.ownerUserId ?? 'unknown'}`}>
                          <TableCell>
                            <ProjectIdentity
                              identity={project.ownerUserId ? userIdentitiesQuery.data?.[project.ownerUserId] : undefined}
                              isLoading={userIdentitiesQuery.isLoading}
                              item={project}
                            />
                          </TableCell>
                          <TableCell title={formatTime(project.latestRunAt)}>{formatRelativeTime(project.latestRunAt)}</TableCell>
                          <TableCell className="numeric">
                            {formatInteger(project.runs)} / {formatInteger(project.workflows)}
                          </TableCell>
                          <TableCell className="numeric">{formatInteger(project.inputFiles)}</TableCell>
                          <TableCell className="numeric">{formatBytes(project.inputBytes)}</TableCell>
                          <TableCell>{failureBadge(project.relatedFailures)}</TableCell>
                          <TableCell className="text-right">
                            <Button asChild variant="ghost">
                              <Link
                                to={{
                                  pathname: '/usage',
                                  search: openSubjectSearch(location.search, 'project', project.projectId),
                                }}
                              >
                                View 360
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableShell>
              </>,
              projects.length === 0,
              'No project-attributed usage was recorded',
            )}
          </div>
        </TabsContent>

        <TabsContent value="storage">
          {inventoryQuery.isLoading ? <LoadingState label="Loading storage usage" /> : null}
          {inventoryQuery.isError ? (
            <ErrorState message={inventoryQuery.error?.message ?? 'Storage usage failed'} onRetry={() => void inventoryQuery.refetch()} />
          ) : null}
          {inventoryQuery.data ? (
            <section className="grid gap-4">
              <StateView
                title={
                  inventoryQuery.data.storage.hasReconciledInventory
                    ? 'All durable storage categories have a completed baseline'
                    : `${inventoryQuery.data.storage.reconciledCategories} of 3 storage categories have a completed baseline`
                }
                tone={inventoryQuery.data.storage.hasReconciledInventory ? 'healthy' : 'coverage'}
              >
                Current totals are object-level facts, not accumulated run inputs.{' '}
                {formatInteger(inventoryQuery.data.storage.reconciledObjects)} live objects were observed by reconciliation. Coverage began{' '}
                {formatTime(inventoryQuery.data.storage.coverageStartedAt)}; latest complete scan{' '}
                {formatTime(inventoryQuery.data.storage.lastReconciledAt)}.
              </StateView>
              <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  ['Current storage', formatBytes(inventoryQuery.data.storage.bytes)],
                  ['Live objects', formatInteger(inventoryQuery.data.storage.objects)],
                  ['Added in period', formatBytes(inventoryQuery.data.storage.createdBytesInWindow)],
                  ['Removed in period', formatBytes(inventoryQuery.data.storage.deletedBytesInWindow)],
                ].map(([label, value]) => (
                  <Card key={label}>
                    <CardContent>
                      <dt className="text-muted text-xs font-semibold">{label}</dt>
                      <dd className="text-ink numeric mt-2 text-xl font-semibold">{value}</dd>
                    </CardContent>
                  </Card>
                ))}
              </dl>
              <div className="grid gap-4 xl:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Storage by durable category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RankedBuckets
                      buckets={inventoryQuery.data.storageByCategory ?? []}
                      emptyTitle="No durable objects are recorded"
                      format={formatBytes}
                      noun="current live bytes"
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Input evidence by workflow</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RankedBuckets
                      buckets={inventoryQuery.data.filesByAnalysisType ?? []}
                      emptyTitle="No workflow input evidence was recorded"
                      noun="input references across runs"
                    />
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Attributed cost estimate</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-muted text-xs font-semibold">Selected-period fact-based compute estimate</p>
                      <p className="text-ink numeric mt-1 text-2xl font-semibold">
                        {inventoryQuery.data.cost.isEstimateAvailable
                          ? formatUsd(inventoryQuery.data.cost.periodEstimatedUsd)
                          : 'Unavailable'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted text-xs font-semibold">Current monthly storage run rate</p>
                      <p className="text-ink numeric mt-1 text-2xl font-semibold">
                        {inventoryQuery.data.cost.isStorageEstimateAvailable
                          ? formatUsd(inventoryQuery.data.cost.monthlyStorageRunRateUsd)
                          : 'Unavailable'}
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {(inventoryQuery.data.cost.components ?? []).map((component) => (
                      <div className="border-line rounded-lg border p-3" key={component.key}>
                        <p className="text-muted text-xs font-semibold">{component.label}</p>
                        <p className="text-ink numeric mt-2 font-semibold">
                          {component.isAvailable ? formatUsd(component.estimatedUsd) : 'Unavailable'}
                        </p>
                        <p className="text-muted mt-1 text-xs">{component.coverage}</p>
                        <p className="text-muted mt-1 text-xs">
                          {component.timeBasis.replaceAll('_', ' ')} · {component.estimateKind.replaceAll('_', ' ')}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-muted text-xs">
                    {inventoryQuery.data.cost.methodology} Rate card: {inventoryQuery.data.cost.rateCardVersion}.
                  </p>
                  <p className="text-muted text-xs">
                    Priced {formatInteger(inventoryQuery.data.cost.pricedOperations)} of{' '}
                    {formatInteger(inventoryQuery.data.cost.totalOperations)} operation facts. Reconciliation:{' '}
                    {(inventoryQuery.data.cost.reconciliationStatus ?? 'not_configured').replaceAll('_', ' ')}.{' '}
                    {inventoryQuery.data.cost.reconciliationNote}
                  </p>
                  {(inventoryQuery.data.cost.assumptions ?? []).length > 0 ? (
                    <ul className="text-muted list-disc pl-5 text-xs">
                      {(inventoryQuery.data.cost.assumptions ?? []).map((assumption) => (
                        <li key={assumption}>{assumption}</li>
                      ))}
                    </ul>
                  ) : null}
                </CardContent>
              </Card>
            </section>
          ) : null}
        </TabsContent>

        <TabsContent value="workflows">
          <section className="grid gap-4">
            {inventoryQuery.isLoading ? <LoadingState label="Loading workflow usage" /> : null}
            {inventoryQuery.isError ? (
              <ErrorState
                message={inventoryQuery.error?.message ?? 'Workflow usage failed'}
                onRetry={() => void inventoryQuery.refetch()}
              />
            ) : null}
            {inventoryQuery.data ? (
              <TableShell>
                <Table aria-label="Workflow coverage scorecard" style={{ minWidth: '56rem' }}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Runs</TableHead>
                      <TableHead>Users / projects</TableHead>
                      <TableHead>Timed runs</TableHead>
                      <TableHead>P50 / P95</TableHead>
                      <TableHead>Latest evidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(inventoryQuery.data.workflowCoverage ?? []).map((workflow) => (
                      <TableRow key={workflow.analysisType}>
                        <TableCell className="text-ink font-semibold">{humanize(workflow.analysisType)}</TableCell>
                        <TableCell>
                          <Badge tone={(workflow.runs ?? 0) > 0 ? 'green' : 'slate'}>
                            {(workflow.runs ?? 0) > 0 ? 'covered' : 'no evidence'}
                          </Badge>
                        </TableCell>
                        <TableCell className="numeric">{formatInteger(workflow.runs)}</TableCell>
                        <TableCell className="numeric">
                          {formatInteger(workflow.users)} / {formatInteger(workflow.projects)}
                        </TableCell>
                        <TableCell className="numeric">{formatInteger(workflow.timedRuns)}</TableCell>
                        <TableCell className="numeric">
                          {formatDuration(workflow.duration.p50, workflow.duration.unit)} /{' '}
                          {formatDuration(workflow.duration.p95, workflow.duration.unit)}
                        </TableCell>
                        <TableCell title={formatTime(workflow.lastRunAt)}>{formatRelativeTime(workflow.lastRunAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableShell>
            ) : null}
          </section>
        </TabsContent>
      </Tabs>

      <UsageSubjectDrawer
        close={() =>
          void navigate({
            pathname: '/usage',
            search: closeSubjectSearch(location.search),
          })
        }
        currentSearch={location.search}
        identityLabel={selectedIdentityLabel}
        query={subjectQuery}
        subjectId={selectedSubjectId}
        subjectType={selectedSubjectType}
      />

      <div className="text-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>Generated {formatTime(summary?.generatedAt)}</span>
        <span>{hasRunEvidence ? `Latest run ${formatRelativeTime(runEvidence?.lastObservedAt)}` : 'Run freshness unavailable'}</span>
      </div>
    </div>
  );
};
